import { useRef, useEffect, useState, useMemo } from 'react'
import { useProject } from '../stores/ProjectContext'
import { useI18n } from '../stores/I18nContext'
import { useIsMobile } from '../hooks/useIsMobile'
import InsightsBar from './InsightsBar'
import InsightsPanel from './InsightsPanel'
import CanvasToolPalette from './CanvasToolPalette'
import './Canvas.css'

const TOUCH_HINT_KEY = 'tdo-touch-hint-dismissed'

export default function Canvas() {
  const canvasRef = useRef(null)
  const {
    project,
    selectedFlow,
    selectedCheckpoint,
    timeView,
    currentProjectId,
    setSelectedFlow,
    setSelectedCheckpoint,
    addCheckpoint
  } = useProject()
  const { t, language } = useI18n()
  const isMobile = useIsMobile()
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 })
  const [insightsPanelOpen, setInsightsPanelOpen] = useState(false)
  const [touchHintDismissed, setTouchHintDismissed] = useState(() => {
    try { return localStorage.getItem(TOUCH_HINT_KEY) === '1' } catch { return false }
  })

  // Canvas pan / tool state. Pan is local view state and is NOT persisted to
  // the project. It exists so users can slide the chart out from underneath
  // overlay panels (Flow properties, AI assistant) without resizing the SVG.
  const [tool, setTool] = useState('select') // 'select' | 'hand'
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const spaceHeldRef = useRef(false)
  const [spaceHeld, setSpaceHeld] = useState(false)
  const panDragRef = useRef(null) // { startX, startY, startPanX, startPanY, pointerId }
  const didPanRef = useRef(false) // suppress click after a pan-drag

  const resetPan = () => setPan({ x: 0, y: 0 })

  // Reset pan when switching projects.
  useEffect(() => {
    setPan({ x: 0, y: 0 })
  }, [currentProjectId])

  // Hold Space to temporarily pan; Esc returns to Select tool.
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false
      const tag = el.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
    }
    const onKeyDown = (e) => {
      if (e.code === 'Space' && !isTypingTarget(e.target)) {
        if (!spaceHeldRef.current) {
          spaceHeldRef.current = true
          setSpaceHeld(true)
        }
        e.preventDefault()
      } else if (e.key === 'Escape') {
        setTool('select')
      }
    }
    const onKeyUp = (e) => {
      if (e.code === 'Space') {
        spaceHeldRef.current = false
        setSpaceHeld(false)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [])

  const dismissTouchHint = () => {
    try { localStorage.setItem(TOUCH_HINT_KEY, '1') } catch {}
    setTouchHintDismissed(true)
  }

  // Layout constants
  const PADDING = { top: 60, right: 40, bottom: 80, left: 100 }
  const AXIS_LABEL_WIDTH = 60

  // Get axis ranges from project settings
  const TIME_RANGE = useMemo(() => {
    return (project.xAxisEnd || 12) - (project.xAxisStart || 0)
  }, [project.xAxisEnd, project.xAxisStart])

  const DISTANCE_RANGE = useMemo(() => {
    const labels = project.distanceLabels || []
    return labels.length > 0 ? labels.length - 1 : 4
  }, [project.distanceLabels])

  const xAxisStart = project.xAxisStart || 0

  useEffect(() => {
    const updateDimensions = () => {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect()
        setDimensions({ width: rect.width, height: rect.height })
      }
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  // Calculate pixel positions
  const getX = (time) => {
    const chartWidth = dimensions.width - PADDING.left - PADDING.right - AXIS_LABEL_WIDTH
    const relativeTime = time - xAxisStart
    return PADDING.left + AXIS_LABEL_WIDTH + (relativeTime / TIME_RANGE) * chartWidth
  }

  const getY = (distance) => {
    const chartHeight = dimensions.height - PADDING.top - PADDING.bottom
    return PADDING.top + (distance / DISTANCE_RANGE) * chartHeight
  }

  // Convert pixel coordinates back to time/distance
  const getTimeFromX = (x) => {
    const chartWidth = dimensions.width - PADDING.left - PADDING.right - AXIS_LABEL_WIDTH
    const relativeX = x - PADDING.left - AXIS_LABEL_WIDTH
    const time = xAxisStart + (relativeX / chartWidth) * TIME_RANGE
    return Math.round(time * 2) / 2 // Snap to 0.5
  }

  const getDistanceFromY = (y) => {
    const chartHeight = dimensions.height - PADDING.top - PADDING.bottom
    const relativeY = y - PADDING.top
    return Math.round((relativeY / chartHeight) * DISTANCE_RANGE)
  }

  // Handle canvas click to add checkpoint
  const handleCanvasClick = (e) => {
    // Suppress click that ended a pan-drag.
    if (didPanRef.current) {
      didPanRef.current = false
      return
    }
    // Hand tool and space-pan modifier do not add checkpoints.
    if (tool === 'hand' || spaceHeldRef.current) return
    if (!selectedFlow) return

    // On touch / mobile, we do NOT support add-by-tap (too easy to mis-add
    // while panning). Users add checkpoints on desktop.
    if (isMobile || e.pointerType === 'touch' || e.nativeEvent?.pointerType === 'touch') return

    const rect = canvasRef.current.getBoundingClientRect()
    // Account for current pan offset: a click that visually lands inside
    // the (panned) chart should resolve to the underlying time/distance.
    const x = e.clientX - rect.left - pan.x
    const y = e.clientY - rect.top - pan.y

    // Check if click is within the chart area
    if (x < PADDING.left + AXIS_LABEL_WIDTH || x > dimensions.width - PADDING.right) return
    if (y < PADDING.top || y > dimensions.height - PADDING.bottom) return

    const time = getTimeFromX(x)
    const distance = getDistanceFromY(y)

    // Get the selected flow to count existing checkpoints
    const flow = project.flows.find(f => f.id === selectedFlow)
    const checkpointNumber = flow ? flow.checkpoints.length + 1 : 1

    const checkpoint = addCheckpoint(
      selectedFlow,
      `${t('checkpoint.typeCheckpoint')} ${checkpointNumber}`,
      time,
      distance
    )

    setSelectedCheckpoint(checkpoint.id)
  }

  // Get subtask progress for a checkpoint
  const getSubtaskProgress = (checkpoint) => {
    if (!checkpoint.subtasks || checkpoint.subtasks.length === 0) return null
    const done = checkpoint.subtasks.filter(s => s.done).length
    return done / checkpoint.subtasks.length
  }

  // Calculate cumulative times for proportional view (only visible flows)
  const getProportionalData = useMemo(() => {
    if (timeView !== 'proportional') return null

    let maxCumulativeTime = 0
    const flowCumulativeTimes = {}

    project.flows.filter(f => !f.hidden).forEach(flow => {
      let cumulative = 0
      const sortedCps = [...flow.checkpoints].sort((a, b) => a.time - b.time)
      flowCumulativeTimes[flow.id] = sortedCps.map(cp => {
        cumulative += (cp.estimatedDuration || 30) // Default 30 min if not set
        return cumulative
      })
      if (cumulative > maxCumulativeTime) {
        maxCumulativeTime = cumulative
      }
    })

    return { flowCumulativeTimes, maxCumulativeTime }
  }, [timeView, project.flows])

  // Get X position based on view mode
  const getXForCheckpoint = (flow, cpIndex, cp) => {
    if (timeView === 'proportional' && getProportionalData) {
      const { flowCumulativeTimes, maxCumulativeTime } = getProportionalData
      if (maxCumulativeTime > 0) {
        const cumTime = flowCumulativeTimes[flow.id]?.[cpIndex] || 0
        const chartWidth = dimensions.width - PADDING.left - PADDING.right - AXIS_LABEL_WIDTH
        return PADDING.left + AXIS_LABEL_WIDTH + (cumTime / maxCumulativeTime) * chartWidth
      }
    }
    // Default: compact view uses time-based position
    return getX(cp.time)
  }

  // Render flow lines and checkpoints
  const renderFlow = (flow) => {
    const isSelected = selectedFlow === flow.id
    const sortedCheckpoints = [...flow.checkpoints].sort((a, b) => a.time - b.time)

    if (sortedCheckpoints.length === 0) return null

    // Build path with view-mode aware X positions
    const points = sortedCheckpoints.map((cp, idx) => ({
      x: getXForCheckpoint(flow, idx, cp),
      y: getY(cp.distance),
      ...cp
    }))

    return (
      <g key={flow.id} className="flow-group">
        {/* Base line (light) */}
        {points.length > 1 && (
          <path
            d={points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')}
            fill="none"
            stroke={flow.color}
            strokeWidth={isSelected ? 4 : 3}
            strokeOpacity={0.3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {/* Progress lines (colored based on completion) */}
        {points.slice(0, -1).map((fromPt, i) => {
          const toPt = points[i + 1]
          const progress = getSubtaskProgress(toPt)

          if (toPt.status === 'completed') {
            // Full segment for completed checkpoints
            return (
              <path
                key={`progress-${i}`}
                d={`M ${fromPt.x} ${fromPt.y} L ${toPt.x} ${toPt.y}`}
                fill="none"
                stroke={flow.color}
                strokeWidth={isSelected ? 4 : 3}
                strokeLinecap="round"
              />
            )
          } else if (progress !== null && progress > 0) {
            // Partial segment based on subtask progress
            const progressX = fromPt.x + (toPt.x - fromPt.x) * progress
            const progressY = fromPt.y + (toPt.y - fromPt.y) * progress
            return (
              <path
                key={`progress-${i}`}
                d={`M ${fromPt.x} ${fromPt.y} L ${progressX} ${progressY}`}
                fill="none"
                stroke={flow.color}
                strokeWidth={isSelected ? 4 : 3}
                strokeLinecap="round"
              />
            )
          }
          return null
        })}

        {/* Checkpoints */}
        {points.map((point) => {
          const isCheckpointSelected = selectedCheckpoint === point.id
          const radius = point.type === 'milestone' ? 10 : point.type === 'gate' ? 8 : 6

          return (
            <g
              key={point.id}
              className="checkpoint"
              onClick={(e) => {
                if (tool === 'hand' || spaceHeldRef.current || didPanRef.current) {
                  didPanRef.current = false
                  return
                }
                e.stopPropagation()
                setSelectedFlow(flow.id)
                setSelectedCheckpoint(point.id)
              }}
            >
              {point.type === 'gate' ? (
                <rect
                  x={point.x - radius}
                  y={point.y - radius}
                  width={radius * 2}
                  height={radius * 2}
                  fill={point.status === 'completed' ? flow.color : 'var(--bg-tertiary)'}
                  stroke={flow.color}
                  strokeWidth={isCheckpointSelected ? 3 : 2}
                  transform={`rotate(45 ${point.x} ${point.y})`}
                  style={{ cursor: 'pointer' }}
                />
              ) : (
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={radius}
                  fill={point.status === 'completed' ? flow.color : 'var(--bg-tertiary)'}
                  stroke={flow.color}
                  strokeWidth={isCheckpointSelected ? 3 : 2}
                  style={{ cursor: 'pointer' }}
                />
              )}

              {/* Checkpoint label */}
              <text
                x={point.x}
                y={point.y - radius - 8}
                textAnchor="middle"
                fill="var(--text-primary)"
                fontSize="11"
              >
                {point.name}
              </text>
            </g>
          )
        })}
      </g>
    )
  }

  // Render grid and axes
  const renderGrid = () => {
    const gridLines = []

    // Vertical grid lines (time)
    for (let t = 0; t <= TIME_RANGE; t++) {
      const x = getX(xAxisStart + t)
      gridLines.push(
        <line
          key={`v-${t}`}
          x1={x}
          y1={PADDING.top}
          x2={x}
          y2={dimensions.height - PADDING.bottom}
          stroke="var(--border-color)"
          strokeDasharray={t % 4 === 0 ? 'none' : '2,4'}
        />
      )
    }

    // Horizontal grid lines (distance)
    for (let d = 0; d <= DISTANCE_RANGE; d++) {
      const y = getY(d)
      gridLines.push(
        <line
          key={`h-${d}`}
          x1={PADDING.left + AXIS_LABEL_WIDTH}
          y1={y}
          x2={dimensions.width - PADDING.right}
          y2={y}
          stroke="var(--border-color)"
        />
      )
    }

    return gridLines
  }

  // Render axes
  const renderAxes = () => {
    const timeLabels = []
    const distanceLabelsElements = []

    // Time labels - show every 2 units
    const step = TIME_RANGE > 20 ? 4 : 2
    for (let t = 0; t <= TIME_RANGE; t += step) {
      timeLabels.push(
        <text
          key={`time-${t}`}
          x={getX(xAxisStart + t)}
          y={dimensions.height - PADDING.bottom + 20}
          textAnchor="middle"
          fill="var(--text-secondary)"
          fontSize="11"
        >
          {xAxisStart + t}
        </text>
      )
    }

    // Distance labels from project settings
    const labels = project.distanceLabels?.length > 0
      ? project.distanceLabels
      : ['Start', 'Phase 1', 'Phase 2', 'Phase 3', 'Complete']

    labels.forEach((label, i) => {
      distanceLabelsElements.push(
        <text
          key={`dist-${i}`}
          x={PADDING.left + AXIS_LABEL_WIDTH - 10}
          y={getY(i) + 4}
          textAnchor="end"
          fill="var(--text-secondary)"
          fontSize="11"
        >
          {label}
        </text>
      )
    })

    return [...timeLabels, ...distanceLabelsElements]
  }

  // Empty state
  if (project.flows.length === 0) {
    return (
      <>
        <div className="canvas-wrapper">
          <div className="canvas-container" ref={canvasRef}>
            <div className="empty-state">
              <div className="empty-icon">🚂</div>
              <h2>{t('empty.noFlows')}</h2>
              <p>{t('empty.addFirstFlow')}</p>
            </div>
          </div>
          <InsightsBar onConflictsClick={() => setInsightsPanelOpen(true)} />
        </div>
        <InsightsPanel isOpen={insightsPanelOpen} onClose={() => setInsightsPanelOpen(false)} />
      </>
    )
  }

  // Get selected flow info for hint
  const currentFlow = project.flows.find(f => f.id === selectedFlow)

  // Pan drag handlers — active when Hand tool, Space held, or middle-click.
  const handlePointerDown = (e) => {
    const wantsPan =
      tool === 'hand' ||
      spaceHeldRef.current ||
      e.button === 1
    if (!wantsPan) return
    // Don't initiate pan from interactive controls inside the canvas.
    if (e.target.closest && e.target.closest('.canvas-tool-palette')) return
    panDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPanX: pan.x,
      startPanY: pan.y,
      pointerId: e.pointerId
    }
    didPanRef.current = false
    setIsPanning(true)
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch {}
    e.preventDefault()
  }

  const handlePointerMove = (e) => {
    const drag = panDragRef.current
    if (!drag) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY
    if (!didPanRef.current && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) {
      didPanRef.current = true
    }
    setPan({ x: drag.startPanX + dx, y: drag.startPanY + dy })
  }

  const handlePointerUp = (e) => {
    const drag = panDragRef.current
    if (!drag) return
    panDragRef.current = null
    setIsPanning(false)
    try { e.currentTarget.releasePointerCapture(drag.pointerId) } catch {}
  }

  const containerClasses = [
    'canvas-container',
    selectedFlow && !isMobile && tool === 'select' && !spaceHeld ? 'can-add-checkpoint' : '',
    tool === 'hand' || spaceHeld ? 'tool-hand' : '',
    isPanning ? 'panning' : ''
  ].filter(Boolean).join(' ')

  const canReset = pan.x !== 0 || pan.y !== 0

  return (
    <div className="canvas-wrapper">
      {isMobile && !touchHintDismissed && (
        <div className="canvas-touch-hint" role="status">
          <span>
            {language === 'nb'
              ? 'Tips: Trykk på en sjekkpunkt for å se detaljer. Legg til og flytt på desktop.'
              : 'Tip: Tap a checkpoint to view details. Add and move on desktop.'}
          </span>
          <button
            type="button"
            onClick={dismissTouchHint}
            aria-label={language === 'nb' ? 'Lukk' : 'Dismiss'}
          >
            ×
          </button>
        </div>
      )}
      <div
        className={containerClasses}
        ref={canvasRef}
        onClick={handleCanvasClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <CanvasToolPalette
          tool={tool}
          setTool={setTool}
          onReset={resetPan}
          canReset={canReset}
        />

        <svg
          width={dimensions.width}
          height={dimensions.height}
          className="canvas-svg"
        >
          {/* Axis legends */}
          <text
            x={PADDING.left / 2}
            y={dimensions.height / 2}
            textAnchor="middle"
            fill="var(--text-muted)"
            fontSize="12"
            transform={`rotate(-90 ${PADDING.left / 2} ${dimensions.height / 2})`}
          >
            {t('project.distanceLabels').split(' ')[0] || 'Distance'}
          </text>

          <text
            x={(dimensions.width + PADDING.left) / 2}
            y={dimensions.height - 20}
            textAnchor="middle"
            fill="var(--text-muted)"
            fontSize="12"
          >
            {t('checkpoint.time')} ({t(`project.${project.timeUnit}`)})
          </text>

          {/* Panned chart content */}
          <g transform={`translate(${pan.x} ${pan.y})`}>
            {/* Grid */}
            {renderGrid()}

            {/* Axes labels */}
            {renderAxes()}

            {/* Flows: render all visible flows; selected one drawn last so
                it sits on top with its highlighted styling. */}
            {project.flows
              .filter(f => !f.hidden || f.id === selectedFlow)
              .slice()
              .sort((a, b) => {
                if (a.id === selectedFlow) return 1
                if (b.id === selectedFlow) return -1
                return 0
              })
              .map(renderFlow)}
          </g>
        </svg>
      </div>
      <InsightsBar onConflictsClick={() => setInsightsPanelOpen(true)} />
      <InsightsPanel isOpen={insightsPanelOpen} onClose={() => setInsightsPanelOpen(false)} />
    </div>
  )
}
